import React, {
  cloneElement,
  createContext,
  useContext,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useOutsideClick } from '../hooks/useOutsideClick';
import ModalOverlay from './ModalOverlay';
import StyledModal from './StyledModal';

interface ModalContextValue {
  openName: string;
  close: () => void;
  open: (name: string) => void;
}

const ModalContext = createContext<ModalContextValue | null>(null);

function useModal() {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error('useModal must be used within <Modal>');
  return ctx;
}

function Modal({ children }: { children: React.ReactNode }) {
  const [openName, setOpenName] = useState('');

  const close = () => setOpenName('');
  const open = (name: string) => setOpenName(name);

  return (
    <ModalContext.Provider value={{ openName, close, open }}>
      {children}
    </ModalContext.Provider>
  );
}

interface OpenProps {
  children: React.ReactElement<Record<string, unknown>>;
  opens: string;
}

function Open({ children, opens: opensWindowName }: OpenProps) {
  const { open } = useModal();
  return cloneElement(children, { onClick: () => open(opensWindowName) });
}

interface WindowProps {
  children: React.ReactElement<Record<string, unknown>>;
  name: string;
  className?: string;
}

function Window({ children, name, className }: WindowProps) {
  const { openName, close } = useModal();
  const ref = useOutsideClick({ handler: close, listenCapturing: true });

  if (name !== openName) return null;

  return createPortal(
    <ModalOverlay>
      <StyledModal reference={ref} className={className}>
        <div>{cloneElement(children, { onCloseModal: close })}</div>
      </StyledModal>
    </ModalOverlay>,
    document.body,
  );
}

Modal.Open = Open;
Modal.Window = Window;

export default Modal;
